import React, { useState, useEffect, useRef, DragEvent } from 'react';
import { createPortal } from 'react-dom';
import { toast } from '../../../ui/toast';
import { getJobOpeningList } from '../../../utils/api_service/JobOpenings';
import {
  FilePlusIcons,
  SparklesIcons,
  UploadIcons,
  XIcons,
  CircleCheckIcons,
} from '../../../icons/icons';

export interface ExtractedJdData {
  jobTitle: string;
  experience: string;
  technicalSkills: string[];
  behavioralAttributes: string[];
  educations: string[];
  certifications: string[];
  industryExperience: string[];
  searchString: string;
  fileName?: string;
}

interface UseJdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromFile: (file: File) => Promise<void>;
  onCreateFromText: (text: string) => Promise<void>;
  onCreateFromOpening: (jobOpeningId: string) => Promise<void>;
  isCreating?: boolean;
  onAutoFillSearch?: (searchString: string) => void;
  onAutoFillAnalytics?: (extracted: ExtractedJdData) => void;
  onClearSearch?: () => void;
  onClearAnalytics?: () => void;
}

type TabType = 'upload' | 'paste' | 'opening';

export const UseJdModal: React.FC<UseJdModalProps> = ({
  isOpen,
  onClose,
  onCreateFromFile,
  onCreateFromText,
  onCreateFromOpening,
  isCreating = false,
  onAutoFillSearch,
  onAutoFillAnalytics,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('opening');

  // Tab 1: Upload
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Tab 2: Paste
  const [jdText, setJdText] = useState('');

  // Tab 3: Opening
  const [openings, setOpenings] = useState<any[]>([]);
  const [openingsLoading, setOpeningsLoading] = useState(false);
  const [openingSearchQuery, setOpeningSearchQuery] = useState('');
  const [selectedOpeningId, setSelectedOpeningId] = useState<string | null>(null);

  // Extracted data for auto-fill preview
  const [extractedData, setExtractedData] = useState<ExtractedJdData | null>(null);

  useEffect(() => {
    if (isOpen && activeTab === 'opening' && openings.length === 0) {
      fetchOpenings();
    }
  }, [isOpen, activeTab]);

  const fetchOpenings = async () => {
    setOpeningsLoading(true);
    try {
      const res = await getJobOpeningList({} as any, 1, 50);
      if (res && res.json) {
        const data = await res.json();
        const items = data.data || data.openings || data.jobOpenings || data;
        setOpenings(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.error('Failed to fetch job openings', error);
    } finally {
      setOpeningsLoading(false);
    }
  };

  // Rule-based extraction helper for JD text content
  const extractJdInformation = (text: string, fileName: string): ExtractedJdData => {
    const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);

    // 1. Job Title
    let jobTitle = 'Software Engineer';
    const titleMatch = text.match(/(?:job title|position|role)\s*:\s*([^\n\r]+)/i);
    if (titleMatch && titleMatch[1]) {
      jobTitle = titleMatch[1].trim();
    } else if (lines.length > 0) {
      const firstLine = lines[0].replace(/^(job description|jd|position)\s*[-:]?\s*/i, '');
      if (firstLine.length < 60) {
        jobTitle = firstLine;
      }
    }

    // 2. Experience
    let experience = '3-5 years';
    const expMatch = text.match(/(\d+\s*[-–to]*\s*\d*|\d+\+?)\s*(?:years?|yrs?)(?:\s+of)?\s+experience/i);
    if (expMatch) {
      experience = expMatch[0];
    }

    // 3. Technical Skills
    const commonSkills = [
      'React', 'React.js', 'Node.js', 'TypeScript', 'JavaScript', 'Python', 'Java', 'C++',
      'AWS', 'Docker', 'Kubernetes', 'SQL', 'PostgreSQL', 'MongoDB', 'GraphQL', 'REST API',
      'Git', 'CI/CD', 'Agile', 'HTML', 'CSS', 'Tailwind', 'Next.js', 'Redux', 'UiPath',
      'RPA', 'Computer Vision', 'Process Mining', 'Data Science', 'Machine Learning',
      'PHP', 'C#', '.NET', 'Angular', 'Vue.js', 'GCP', 'Azure', 'Microservices'
    ];
    const technicalSkills: string[] = [];
    commonSkills.forEach((skill) => {
      const regex = new RegExp(`\\b${skill.replace('.', '\\.')}\\b`, 'i');
      if (regex.test(text) && !technicalSkills.includes(skill)) {
        technicalSkills.push(skill);
      }
    });

    if (technicalSkills.length === 0) {
      technicalSkills.push('JavaScript', 'TypeScript', 'React', 'Node.js');
    }

    // 4. Behavioral
    const commonBehaviors = [
      'Communication', 'Leadership', 'Problem Solving', 'Teamwork',
      'Analytical Thinking', 'Time Management', 'Adaptability', 'Creativity',
      'Attention to Detail', 'Stakeholder Management'
    ];
    const behavioralAttributes: string[] = [];
    commonBehaviors.forEach((attr) => {
      const regex = new RegExp(`\\b${attr}\\b`, 'i');
      if (regex.test(text) && !behavioralAttributes.includes(attr)) {
        behavioralAttributes.push(attr);
      }
    });
    if (behavioralAttributes.length === 0) {
      behavioralAttributes.push('Communication', 'Problem Solving', 'Teamwork');
    }

    // Clean search string for local search query (use title so it matches mock candidates)
    const searchString = jobTitle;

    return {
      jobTitle,
      experience,
      technicalSkills,
      behavioralAttributes,
      educations: ["Bachelor's Degree in Computer Science"],
      certifications: [],
      industryExperience: [],
      searchString,
      fileName,
    };
  };

  // Primary Action: Real AI search saved to history
  const handleSearchCandidates = async () => {
    try {
      if (activeTab === 'upload' && selectedFile) {
        await onCreateFromFile(selectedFile);
        onClose();
      } else if (activeTab === 'paste' && jdText.trim()) {
        await onCreateFromText(jdText.trim());
        onClose();
      } else if (activeTab === 'opening' && selectedOpeningId) {
        await onCreateFromOpening(selectedOpeningId);
        onClose();
      }
    } catch (err) {
      console.error('Failed to create search', err);
    }
  };

  const handleFileProcess = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit', { position: 'bottom-center' });
      return;
    }

    setSelectedFile(file);

    // If it's a text file, extract text for local preview
    if (file.type.includes('text') || file.name.endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = (e.target?.result as string) || '';
        setExtractedData(extractJdInformation(text, file.name));
      };
      reader.readAsText(file);
    } else {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      setExtractedData({
        jobTitle: cleanName,
        experience: 'Analyzed on search',
        technicalSkills: ['AI-extracted skills'],
        behavioralAttributes: ['AI-extracted traits'],
        educations: [],
        certifications: [],
        industryExperience: [],
        searchString: cleanName,
        fileName: file.name,
      });
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileProcess(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileProcess(e.target.files[0]);
    }
  };

  const handleTextChange = (text: string) => {
    setJdText(text);
    if (text.trim().length > 20) {
      setExtractedData(extractJdInformation(text, 'Pasted JD'));
    }
  };

  const handleAutoFillSearchClick = () => {
    const data = extractedData || (jdText.trim() ? extractJdInformation(jdText, 'Pasted JD') : null);
    if (!data) {
      toast.warning('Please provide a JD file or text first.', { position: 'bottom-center' });
      return;
    }
    if (onAutoFillSearch) {
      onAutoFillSearch(data.searchString);
      toast.success(`Talent Pool search set to "${data.searchString}"!`, { position: 'bottom-center' });
      onClose();
    }
  };

  const handleAutoFillAnalyticsClick = () => {
    const data = extractedData || (jdText.trim() ? extractJdInformation(jdText, 'Pasted JD') : null);
    if (!data) {
      toast.warning('Please provide a JD file or text first.', { position: 'bottom-center' });
      return;
    }
    if (onAutoFillAnalytics) {
      onAutoFillAnalytics(data);
      toast.success('Analytic Config populated with extracted criteria!', { position: 'bottom-center' });
      onClose();
    }
  };

  if (!isOpen) return null;

  const isSearchDisabled =
    isCreating ||
    (activeTab === 'upload' && !selectedFile) ||
    (activeTab === 'paste' && !jdText.trim()) ||
    (activeTab === 'opening' && !selectedOpeningId);

  const filteredOpenings = openings.filter((op: any) =>
    (op.title || op.name || op.jobTitle || '').toLowerCase().includes(openingSearchQuery.toLowerCase())
  );

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/60 backdrop-blur-xs p-4">
      <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden border border-slate-200/90 flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-black tracking-tight text-white">Create New Search</h2>
            <p className="text-xs text-slate-400 font-medium">
              Extract requirements and search your candidate pool
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-full hover:bg-slate-800"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-slate-200 px-6 pt-3 gap-6 bg-slate-50">
          {[
            {
              id: 'opening',
              label: 'From Opening',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              id: 'paste',
              label: 'Paste JD',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              ),
            },
            {
              id: 'upload',
              label: 'Upload JD',
              icon: (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              ),
            },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`flex items-center gap-2 pb-3 px-1 text-xs font-bold transition-all border-b-2 ${
                  isActive
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <span className={isActive ? 'text-indigo-600' : 'text-slate-400'}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* TAB 1: UPLOAD JD */}
          {activeTab === 'upload' && (
            <div className="space-y-3">
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(e.type === 'dragover');
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-all ${
                  isDragOver
                    ? 'border-indigo-500 bg-indigo-50/50'
                    : 'border-slate-300 bg-slate-50/50 hover:border-indigo-400 hover:bg-slate-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileInputChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-100 text-slate-500">
                    <UploadIcons className="h-6 w-6 text-indigo-600" />
                  </div>
                  <p className="text-xs font-bold text-slate-800">
                    Click or drag your JD file here
                  </p>
                  <p className="text-[11px] text-slate-400 font-medium">
                    Supports PDF, DOCX, and TXT (Max 10MB)
                  </p>
                </div>
              </div>

              {selectedFile && (
                <div className="flex items-center justify-between gap-3 bg-emerald-50/80 border border-emerald-200/80 rounded-xl px-4 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-xs font-bold text-emerald-800 uppercase px-1.5 py-0.5 rounded bg-emerald-100 shrink-0">
                      {selectedFile.name.split('.').pop() || 'FILE'}
                    </span>
                    <span className="text-xs font-bold text-emerald-950 truncate">
                      {selectedFile.name}
                    </span>
                    <span className="text-[10px] font-medium text-emerald-600 shrink-0">
                      ({(selectedFile.size / 1024).toFixed(0)} KB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setExtractedData(null);
                    }}
                    className="text-emerald-700 hover:text-red-600 p-1 rounded transition-colors text-xs font-bold"
                    title="Remove file"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TAB 2: PASTE JD */}
          {activeTab === 'paste' && (
            <div className="space-y-2">
              <textarea
                value={jdText}
                onChange={(e) => handleTextChange(e.target.value)}
                placeholder="Paste the complete job description, required skills, and qualifications here..."
                rows={9}
                className="w-full p-3.5 border border-slate-200 rounded-xl bg-slate-50/50 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none leading-relaxed text-slate-800"
              />
              <div className="flex items-center justify-between text-[11px] text-slate-400 px-1">
                <span>Word count: {jdText.trim() ? jdText.trim().split(/\s+/).length : 0} words</span>
                <span>{jdText.length} characters</span>
              </div>
            </div>
          )}

          {/* TAB 3: FROM OPENING */}
          {activeTab === 'opening' && (
            <div className="space-y-3">
              <div className="relative">
                <svg
                  className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search openings (including closed)..."
                  className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                  value={openingSearchQuery}
                  onChange={(e) => setOpeningSearchQuery(e.target.value)}
                />
              </div>

              <div className="overflow-y-auto border border-slate-200 rounded-xl bg-slate-50/50 max-h-[220px]">
                {openingsLoading ? (
                  <div className="flex items-center justify-center h-36 text-slate-400">
                    <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : filteredOpenings.length === 0 ? (
                  <div className="flex items-center justify-center h-36 text-xs text-slate-400">
                    No openings found.
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {filteredOpenings.map((op: any) => {
                      const id = op._id || op.id;
                      const isSelected = selectedOpeningId === id;
                      return (
                        <div
                          key={id}
                          onClick={() => {
                            setSelectedOpeningId(id);
                            const title = op.title || op.name || op.jobTitle || 'Job Opening';
                            setExtractedData({
                              jobTitle: title,
                              experience: 'From Opening',
                              technicalSkills: op.skills || ['Full Stack'],
                              behavioralAttributes: [],
                              educations: [],
                              certifications: [],
                              industryExperience: [],
                              searchString: title,
                              fileName: title,
                            });
                          }}
                          className={`p-3 cursor-pointer transition-all flex justify-between items-center ${
                            isSelected
                              ? 'bg-indigo-50/80 border-l-4 border-indigo-600'
                              : 'hover:bg-slate-100/50'
                          }`}
                        >
                          <div className="flex flex-col">
                            <span className={`text-xs font-bold ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>
                              {op.title || op.name || op.jobTitle || 'Untitled Opening'}
                            </span>
                            {op.jobReferenceNumber && (
                              <span className="text-[10px] text-slate-400 font-mono">
                                #{op.jobReferenceNumber}
                              </span>
                            )}
                          </div>
                          {op.status && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200/80 text-slate-700">
                              {op.status}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Extracted Preview Badge (if available) */}
          {extractedData && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-900">
                  <CircleCheckIcons className="h-3.5 w-3.5 text-indigo-600" />
                  Target Title: {extractedData.jobTitle}
                </span>
                <span className="text-[10px] font-semibold text-slate-500">
                  {extractedData.experience}
                </span>
              </div>
              {extractedData.technicalSkills.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {extractedData.technicalSkills.slice(0, 6).map((sk, idx) => (
                    <span
                      key={idx}
                      className="rounded-md border border-indigo-200/60 bg-white px-1.5 py-0.2 text-[10px] font-medium text-indigo-700"
                    >
                      {sk}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick Auto-Fill Actions */}
          <div className="pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
            <span className="text-[11px] font-bold text-slate-500">Also Auto-Fill:</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAutoFillSearchClick}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-700 transition-colors shadow-2xs"
                title="Populate Talent Pool search bar with target title"
              >
                <SparklesIcons className="h-3 w-3 text-indigo-500" />
                Talent Pool
              </button>
              <button
                type="button"
                onClick={handleAutoFillAnalyticsClick}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-bold text-slate-700 transition-colors shadow-2xs"
                title="Populate 3D graph analytic filters"
              >
                <SparklesIcons className="h-3 w-3 text-emerald-500" />
                Analytics Config
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between">
          <p className="text-[11px] text-slate-400 font-medium">
            Search will be saved under <span className="font-bold text-slate-600">Advanced Search</span>
          </p>
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSearchCandidates}
              disabled={isSearchDisabled}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl shadow-md shadow-indigo-200 active:scale-[0.98] transition-all"
            >
              {isCreating && (
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {isCreating ? 'Processing...' : 'Create Search'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
